import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import {
    Button,
    CircularProgress,
    Divider,
    Grid,
    SelectInput,
    TextFieldLabel,
    Typography
} from 'src/components/UI'
import BackButton from 'src/components/UI/CustomUI/atoms/BackButton'
import CustomCheckbox from 'src/components/UI/CustomUI/atoms/CustomCheckbox'
import { repairItemActions } from 'src/ducks/actions'
import { getRepairlistFilter } from 'src/ducks/filters/selector'
import { getMaterials, getPriceList, getRepairItem } from 'src/ducks/selectors'
import { isEmpty, ITEM_CATEGORY_OPTIONS, SEVERITY_OPTIONS } from 'src/helpers'
import Alert from './Alert'
import ItemTabs from './ItemTabs'
import MaterialTable from './MaterialTable'
import MaterialModal from './Modals/MaterialModal'
import useStyles from './styles'
import { StateProps } from './types'

const RepairItemView = () => {
  const dispatch = useDispatch()

  const { id } = useParams()

  const styles = useStyles()

  const repairItem = useSelector(getRepairItem)
  const priceList = useSelector(getPriceList)
  const repairListFilter = useSelector(getRepairlistFilter())
  const materials = useSelector(getMaterials)

  const priceItem =
    priceList[repairListFilter.selectedTerritoryIndexes?.at(0) || 0]

  const [open, setOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [repairState, setRepairState] = useState<StateProps>({
    ...repairItem,
  })
  const [priceState, setPriceState] = useState<StateProps>({ ...priceItem })
  const [marginInput, setMarginInput] = useState<string>('')
  const [priceInput, setPriceInput] = useState<string>('')

  // Debounce margin input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (marginInput !== '') {
        handlePriceStateUpdate('unitMargin', marginInput)
        setMarginInput('')
        setPriceInput('')
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [marginInput])

  // Debounce price input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (priceInput !== '') {
        handlePriceStateUpdate('unitPrice', priceInput)
        setPriceInput('')
        setMarginInput('')
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [priceInput])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dispatch(repairItemActions.fetchRepairItem(id || '', (succ: boolean) => { }))
  }, [])

  useEffect(() => {
    setPriceState({ ...priceItem })
  }, [priceItem])

  useEffect(() => {
    setRepairState({ ...repairItem })
  }, [repairItem])

  // Recalculate cost/margin/profit when Labor Hours changes
  useEffect(() => {
    setPriceState((prev) => {
      const newCost = Number(prev.unitMaterialCost || 0) + Number(prev.laborRate || 0) * Number(repairState.laborHours || 0)
      const newPrice = calcPrice(newCost, Number(prev.margin || 0))
      const newProfit = calcProfit(newPrice, newCost)
      return { ...prev, unitCost: newCost, unitPrice: newPrice, profit: newProfit }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repairState.laborHours])

  const getChangedProps = (obj1: StateProps, obj2: StateProps) => {
    let result: StateProps = {}

    Object.keys(obj1).forEach((key) => {
      if (obj1[key] !== obj2[key]) result[key] = obj2[key]
    })

    return result
  }

  const isRepairChanged = useMemo(() => {
    return !isEmpty(getChangedProps(repairItem, repairState))
  }, [repairState, repairItem])

  const isPriceChanged = useMemo(() => {
    return !isEmpty(getChangedProps(priceItem || {}, priceState))
  }, [priceState, priceItem])

  const handleRepairStateUpdate = (key: string, value: any) => {
    setRepairState({
      ...repairState,
      [key]: value,
    })
  }

  const calcMargin = (price: number, cost: number) => {
    if (cost === 0) return price > 0 ? 100 : 0
    return price > 0 ? ((price - cost) / price) * 100 : 0
  }

  const calcPrice = (cost: number, margin: number) => {
    if (margin <= 0) return cost
    return cost / (1 - margin / 100)
  }

  const calcProfit = (price: number, cost: number) => price - cost

  const handlePriceStateUpdate = (key: string, value: any) => {
    let next = { ...priceState, [key]: value }

    if (key === 'unitMargin') {
      let clampedMargin = Math.max(0, Math.min(99.99, Number(value) || 0))
      const newPrice = calcPrice(Number(priceState.unitCost || 0), clampedMargin)
      const newProfit = calcProfit(newPrice, Number(priceState.unitCost || 0))
      next = { ...next, margin: clampedMargin, unitPrice: newPrice, profit: newProfit }
    } else if (key === 'unitPrice') {
      const newMargin = calcMargin(Number(value), Number(priceState.unitCost || 0))
      const newProfit = calcProfit(Number(value), Number(priceState.unitCost || 0))
      next = { ...next, unitPrice: Number(value), margin: newMargin, profit: newProfit }
    } else if (key === 'unitMaterialCost') {
      const newCost = Number(value) + Number(priceState.laborRate || 0) * Number(repairState.laborHours || 0)
      const newPrice = calcPrice(newCost, Number(priceState.margin || 0))
      const newProfit = calcProfit(newPrice, newCost)
      next = { ...next, unitMaterialCost: Number(value), unitCost: newCost, unitPrice: newPrice, profit: newProfit }
    } else if (key === 'laborRate') {
      const newCost = Number(priceState.unitMaterialCost || 0) + Number(value) * Number(repairState.laborHours || 0)
      const newPrice = calcPrice(newCost, Number(priceState.margin || 0))
      const newProfit = calcProfit(newPrice, newCost)
      next = { ...next, laborRate: Number(value), unitCost: newCost, unitPrice: newPrice, profit: newProfit }
    }

    setPriceState(next)
  }

  const handleSave = () => {
    if (isRepairChanged) {
      setLoading(true)
      dispatch(
        repairItemActions.patchRepairItem(
          getChangedProps(repairItem, repairState),
          (succ) => {
            setLoading(false)
          }
        )
      )
    }
    if (isPriceChanged) {
      setLoading(true)
      if (
        !repairListFilter.selectedTerritoryIndexes?.length
      ) {
        dispatch(
          repairItemActions.patchAllPriceItems(
            getChangedProps(priceItem || {}, priceState),
            (succ) => {
              setLoading(false)
            }
          )
        )
      } else {
        dispatch(
          repairItemActions.patchPriceItems(
            {
              ids: priceList
                .filter((_, index) =>
                  repairListFilter.selectedTerritoryIndexes?.includes(index)
                )
                .map((item) => item.territoryId),
              request: getChangedProps(priceItem || {}, priceState),
            },
            (succ) => {
              setLoading(false)
            }
          )
        )
      }
    }
  }

  return (
    <>
      <BackButton float top="25px" />
      <Grid container spacing={2}>
        <Grid item container className={styles.header} flexDirection="column">
          <Typography className={styles.title}>{repairItem.title}</Typography>
          <Typography className={styles.subtitle}>{`Estimate Item For ${repairListFilter.territory?.label || 'All Territories'
            }`}</Typography>
        </Grid>
        <Grid item container spacing={2} flexDirection="column" xs={6}>
          <Grid item>
            <Alert text="Price Items are available for all territories. You can override the price for each territory. Title, Disclaimer, Category, Severity, labor hours and hide from client changes will apply to all territories." />
          </Grid>
          <Grid item>
            <TextFieldLabel
              label="Display Title"
              value={repairState.title}
              onChange={(e) => handleRepairStateUpdate('title', e.target.value)}
            />
          </Grid>
          <Grid item>
            <TextFieldLabel
              isTextArea
              autoComplete="off"
              label={
                <Typography display="inline" variant="h6">
                  Display Disclaimer
                </Typography>
              }
              placeholder="Display Disclaimer"
              value={repairState.disclaimer || ''}
              size="small"
              onChange={(newSelected) =>
                handleRepairStateUpdate('disclaimer', newSelected.target.value)
              }
              inputProps={{ style: { fontSize: '14px' } }}
              multiline
              textFieldStyle={{ fontSize: '14px' }}
              labelVariant="h6"
            // disabled={vendorIsPaid}
            />
            {/* <SelectInput
              label="Display Disclaimer"
              labelVariant='body1'
              placeholder="Disclaimer Text on Estimate"
              value={disclaimerArray || []}
              options={DISCLAIMER_OPTIONS.map((option) => option.value)}
              size="small"
              onChange={(newSelected: any) => handleRepairStateUpdate('disclaimer', newSelected)}
              onBlur={_handleBlur}
              clearOnBlur
              multiple
              freeSolo
              maxCharacters={40}
            // disabled={vendorIsPaid}
            /> */}
          </Grid>
          <Grid item container spacing={2}>
            <Grid item xs={4}>
              <SelectInput
                labelVariant="body1"
                label="Category"
                // disabled
                value={repairState.category || ''}
                options={ITEM_CATEGORY_OPTIONS.map((option) => option)}
                onChange={(newValue) => {
                  handleRepairStateUpdate('category', newValue.label)
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={4}>
              <SelectInput
                label="Severity"
                labelVariant="body1"
                options={SEVERITY_OPTIONS}
                value={
                  SEVERITY_OPTIONS.find(
                    (option) => option.key === repairState.severity
                  ) || ''
                }
                onChange={(newValue) =>
                  handleRepairStateUpdate('severity', newValue.label)
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextFieldLabel
                label="Labor Hours"
                value={`${repairState.laborHours || '0'}`}
                onChange={(e) =>
                  handleRepairStateUpdate('laborHours', Number(e.target.value))
                }
              />
            </Grid>
          </Grid>
          <Grid item container spacing={2}>
            {/* <Grid item xs={4}>
              <CustomCheckbox
                bgcolor="var(--gray100)"
                label="Fixed Price"
                checked={!!repairState.fixedPrice}
                onClick={() =>
                  handleRepairStateUpdate('fixedPrice', !repairState.fixedPrice)
                }
              />
            </Grid> */}
            <Grid item xs={4}>
              <CustomCheckbox
                bgcolor="var(--gray100)"
                label="Hide from Client"
                checked={!!repairState.hideFromClient}
                onClick={() =>
                  handleRepairStateUpdate(
                    'hideFromClient',
                    !repairState.hideFromClient
                  )
                }
              />
            </Grid>
          </Grid>
          <Grid
            item
            container
            spacing={2}
            display={
              !repairListFilter.selectedTerritoryIndexes?.length
                ? 'none'
                : 'flex'
            }
          >
            <Grid item xs={4}>
              <TextFieldLabel
                label="Price"
                value={priceInput !== '' ? priceInput : `${Number(priceState.unitPrice).toFixed(2) || ''}`}
                prefix="$"
                onChange={(e) =>
                  setPriceInput(e.target.value)
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextFieldLabel
                label="Material Cost"
                value={`${priceState?.unitMaterialCost || '0'}`}
                onChange={(e) =>
                  handlePriceStateUpdate('unitMaterialCost', e.target.value)
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextFieldLabel
                label="Margin %"
                value={marginInput !== '' ? marginInput : `${priceState?.margin !== undefined && priceState?.margin !== null ? Number(priceState.margin).toFixed(2) : ''}`}
                prefix="%"
                onChange={(e) =>
                  setMarginInput(e.target.value)
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextFieldLabel
                label="Cost"
                value={`${Number(priceState?.unitCost).toFixed(2) || ''}`}
                prefix="$"
                disabled
              />
            </Grid>
            <Grid item xs={4}>
              <TextFieldLabel
                label="Profit"
                value={`${Number(priceState?.profit).toFixed(2) || ''}`}
                prefix="$"
                disabled
              />
            </Grid>
            <Grid item xs={4}>
              <TextFieldLabel
                label="Labor Rate"
                value={`${priceState?.laborRate ?? ''}`}
                onChange={(e) =>
                  handlePriceStateUpdate('laborRate', e.target.value)
                }
              />
            </Grid>
          </Grid>
          
          <Grid item>
            <Button
              variant="containedBig"
              disabled={(!isRepairChanged && !isPriceChanged) || Number(priceState.margin) < 0}
              onClick={handleSave}
              endIcon={
                loading ? (
                  <CircularProgress color="inherit" size={18} />
                ) : (
                  <SaveIcon style={{ height: '16px', width: '16px' }} />
                )
              }
            >
              Save
            </Button>
          </Grid>
          <Grid item>
            <Divider orientation="horizontal" />
          </Grid>
          <Grid
            item
            container
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography className={styles.title}>
              Territory Materials: {repairListFilter.territory?.label}
            </Typography>
            <Button
              className={styles.addBtn}
              endIcon={<AddIcon style={{ width: '14px', height: '14px' }} />}
              onClick={() => setOpen(true)}
            >
              Add Material
            </Button>
          </Grid>
          <Grid item>
            <MaterialTable materials={materials} />
          </Grid>
        </Grid>
        <Grid item container xs={6}>
          <ItemTabs />
        </Grid>
      </Grid>
      <MaterialModal open={open} setOpen={setOpen} />
    </>
  )
}

export default RepairItemView
